import { useId, type FC } from "react";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/page/CampaignEmptyState.module.css";

const { emptyState } = TEXT.campaignOverview;

export type EmptyStateVariant = "agency" | "infillion";

interface CampaignEmptyStateProps {
  variant: EmptyStateVariant;
}

const GRADIENT_START = "var(--color-primary-gradient-start)";
const GRADIENT_MID = "var(--color-primary-gradient-mid)";
const GRADIENT_END = "var(--color-primary-gradient-end)";

// Step badges walk the Light Bloom gradient left to right; the final step is
// the solid orange end-stop. A 3-step list is purple→pink, pink→orange, solid
// orange; a 2-step list is purple→orange, solid orange.
const BADGE_BACKGROUNDS: Record<EmptyStateVariant, string[]> = {
  agency: [
    `linear-gradient(135deg, ${GRADIENT_START}, ${GRADIENT_END})`,
    GRADIENT_END,
  ],
  infillion: [
    `linear-gradient(135deg, ${GRADIENT_START}, ${GRADIENT_MID})`,
    `linear-gradient(135deg, ${GRADIENT_MID}, ${GRADIENT_END})`,
    GRADIENT_END,
  ],
};

const STEPS: Record<
  EmptyStateVariant,
  { background: string; description: string; title: string }[]
> = {
  agency: [
    {
      description: emptyState.agency.step1Description,
      title: emptyState.agency.step1Title,
    },
    {
      description: emptyState.agency.step2Description,
      title: emptyState.agency.step2Title,
    },
  ].map((step, index) => ({
    ...step,
    background: BADGE_BACKGROUNDS.agency[index],
  })),
  infillion: [
    {
      description: emptyState.infillion.step1Description,
      title: emptyState.infillion.step1Title,
    },
    {
      description: emptyState.infillion.step2Description,
      title: emptyState.infillion.step2Title,
    },
    {
      description: emptyState.infillion.step3Description,
      title: emptyState.infillion.step3Title,
    },
  ].map((step, index) => ({
    ...step,
    background: BADGE_BACKGROUNDS.infillion[index],
  })),
};

// Petal shape: 20px radius on three corners, 64px on the bottom-right —
// the DS signature asymmetric curve (CSS equivalent: 20px 20px 64px 20px).
const PETAL_PATH =
  "M20,0 L76,0 A20,20 0 0 1 96,20 L96,32 A64,64 0 0 1 32,96 L20,96 A20,20 0 0 1 0,76 L0,20 A20,20 0 0 1 20,0 Z";

const AnalyticsIcon: FC = () => {
  const gradId = useId();
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="96"
      viewBox="0 0 96 96"
      width="96"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={gradId} x1="0%" x2="100%" y1="0%" y2="100%">
          <stop
            offset="0%"
            style={{ stopColor: "var(--color-primary-gradient-start)" }}
          />
          <stop
            offset="50%"
            style={{ stopColor: "var(--color-primary-gradient-mid)" }}
          />
          <stop
            offset="100%"
            style={{ stopColor: "var(--color-primary-gradient-end)" }}
          />
        </linearGradient>
      </defs>
      <path d={PETAL_PATH} fill={`url(#${gradId})`} />
      <rect fill="white" height="20" rx="2" width="10" x="28" y="52" />
      <rect fill="white" height="36" rx="2" width="10" x="43" y="36" />
      <rect fill="white" height="28" rx="2" width="10" x="58" y="44" />
    </svg>
  );
};

const CampaignEmptyState: FC<CampaignEmptyStateProps> = ({ variant }) => {
  const steps = STEPS[variant];
  return (
    <div className={styles.root}>
      <AnalyticsIcon />
      <h2 className={styles.heading}>{emptyState.heading}</h2>
      <p className={styles.description}>{emptyState[variant].description}</p>
      <div className={styles.card}>
        {steps.map((step, index) => (
          <div key={step.title} className={styles.step}>
            <div className={styles.track}>
              <div
                aria-hidden="true"
                className={styles.badge}
                style={{ background: step.background }}
              >
                {index + 1}
              </div>
              {index < steps.length - 1 && <div className={styles.connector} />}
            </div>
            <div className={styles.stepContent}>
              <div className={styles.stepTitle}>{step.title}</div>
              <div className={styles.stepDescription}>{step.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignEmptyState;
